import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { markJustAuthed } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

function readCookie(name) {
  const parts = (document.cookie || "").split(";");
  for (const p of parts) {
    const [k, ...rest] = p.trim().split("=");
    if (k === name) return decodeURIComponent(rest.join("="));
  }
  return null;
}

// How far before the JWT `exp` (epoch seconds) we fire a silent refresh.
// 5 min gives Redis / DB hiccups room to retry once without the user seeing it.
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
// How far before `exp` we surface a "your session is about to expire" toast
// if the silent refresh could not renew the token (e.g. admin force-rotate
// or a persistent network outage). Runs after every refresh attempt so a
// successful refresh cancels the warning by rescheduling with the new exp.
const EXPIRY_WARNING_MS = 60 * 1000;
// Never schedule a refresh sooner than this — protects against `exp` values in
// the past or a clock skew that would otherwise put us in a tight loop.
const MIN_REFRESH_DELAY_MS = 15 * 1000;
// setTimeout stores its delay in a 32-bit signed int (max ≈ 24.8 days). With
// TOKEN_DAYS=30 the naive delay (~2.59e9 ms) overflows and fires instantly,
// producing an unbounded /auth/refresh loop. Clamp to 12 hours and let the
// callback re-check the remaining time until it lands inside the margin.
const MAX_TIMER_DELAY_MS = 12 * 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bootstrap payload (sites/memberships/unread) — populated by /auth/bootstrap so
  // downstream shell components don't have to fire their own parallel GETs.
  const [bootstrap, setBootstrap] = useState(null);
  const refreshTimer = useRef(null);
  const warningTimer = useRef(null);
  const refreshInFlight = useRef(false);
  const currentExp = useRef(0);

  const clearRefreshTimer = () => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    if (warningTimer.current) {
      clearTimeout(warningTimer.current);
      warningTimer.current = null;
    }
  };

  const scheduleExpiryWarning = (expEpochSeconds) => {
    if (warningTimer.current) {
      clearTimeout(warningTimer.current);
      warningTimer.current = null;
    }
    if (!expEpochSeconds) return;
    const msUntilWarn = expEpochSeconds * 1000 - Date.now() - EXPIRY_WARNING_MS;
    if (msUntilWarn <= 0) return; // already past the warning point
    // Same clamp as the refresh timer — 12 h max to dodge the 32-bit overflow.
    const delay = Math.min(msUntilWarn, MAX_TIMER_DELAY_MS);
    warningTimer.current = setTimeout(() => {
      // Only warn if this exp is still the current one (a successful refresh
      // will have rescheduled with a newer exp and this callback would be a
      // no-op).
      if (currentExp.current !== expEpochSeconds) return;
      const secondsLeft = Math.max(1, Math.round((expEpochSeconds * 1000 - Date.now()) / 1000));
      toast.warning(
        `Your session expires in ${secondsLeft}s. Save your work — you'll be signed out shortly.`,
        { duration: EXPIRY_WARNING_MS - 5000, id: "session-expiry-warning" },
      );
    }, delay);
  };

  const scheduleSilentRefresh = (expEpochSeconds) => {
    clearRefreshTimer();
    if (!expEpochSeconds) return;
    currentExp.current = expEpochSeconds;
    const msUntilExpiry = expEpochSeconds * 1000 - Date.now();
    // Aim for `msUntilExpiry - MARGIN` but never below MIN and never above the
    // 32-bit-safe cap — if the token still has a week left, we just wake up in
    // 12 h and re-evaluate. Prevents both the overflow bug (loop) and running
    // the timer straight through a laptop sleep without a check.
    const target = Math.max(msUntilExpiry - REFRESH_MARGIN_MS, MIN_REFRESH_DELAY_MS);
    const delay = Math.min(target, MAX_TIMER_DELAY_MS);
    refreshTimer.current = setTimeout(silentRefresh, delay);
    // Also (re)schedule the 60-second-warning toast. A successful refresh will
    // reschedule with a newer exp so the previous warning becomes a no-op.
    scheduleExpiryWarning(expEpochSeconds);
  };

  const silentRefresh = async () => {
    // Only refresh when we're actually inside the margin window — otherwise
    // just reschedule against the exp we already know about.
    const msLeft = currentExp.current * 1000 - Date.now();
    if (msLeft > REFRESH_MARGIN_MS) {
      scheduleSilentRefresh(currentExp.current);
      return;
    }
    if (refreshInFlight.current) {
      // Someone else is refreshing; try again shortly so the timer never dies.
      refreshTimer.current = setTimeout(silentRefresh, MIN_REFRESH_DELAY_MS);
      return;
    }
    refreshInFlight.current = true;
    try {
      const { data } = await api.post("/auth/refresh");
      if (data?.user) setUser(data.user);
      if (data?.exp) scheduleSilentRefresh(data.exp);
      // A visible expiry-warning toast is now stale — the session was just
      // renewed and the user shouldn't keep seeing "expires in 60s".
      toast.dismiss("session-expiry-warning");
    } catch {
      // Refresh failed (rotated session / expired). Reschedule a short retry so
      // a transient network blip doesn't permanently kill the refresh timer;
      // if the session is genuinely gone, the next 401 from any real request
      // will drive the logout path.
      refreshTimer.current = setTimeout(silentRefresh, MIN_REFRESH_DELAY_MS);
    } finally {
      refreshInFlight.current = false;
    }
  };

  const loadBootstrap = async () => {
    if (!readCookie("eip_authed")) {
      setUser(null);
      setBootstrap(null);
      clearRefreshTimer();
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/bootstrap");
      setUser(data.user);
      setBootstrap({
        sites: data.sites || [],
        memberships: data.memberships || [],
        unread_notifications: data.unread_notifications || 0,
        active_tenant_id: data.active_tenant_id,
      });
      if (data.exp) scheduleSilentRefresh(data.exp);
    } catch {
      setUser(null);
      setBootstrap(null);
      clearRefreshTimer();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBootstrap();
    return clearRefreshTimer;
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Open the login-grace window BEFORE setUser triggers the redirect + parallel
    // page fetches, so any GET that races the browser's cookie commit gets a retry
    // instead of an immediate /login redirect.
    markJustAuthed();
    setUser(data.user);
    // Fire-and-forget bootstrap so the AppShell has sites/memberships ready by the
    // time the redirect renders. Also seeds the silent-refresh timer.
    loadBootstrap();
    return data.user;
  };

  const signup = async (name, email, password) => {
    const { data } = await api.post("/auth/signup", { name, email, password });
    markJustAuthed();
    setUser(data.user);
    loadBootstrap();
    return data.user;
  };

  const logout = async () => {
    clearRefreshTimer();
    try { await api.post("/auth/logout"); } catch (e) { /* best-effort */ }
    setUser(null);
    setBootstrap(null);
    window.location.href = "/login";
  };

  const refreshUser = loadBootstrap;

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshUser, setUser, bootstrap }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
